import { Loader, Title } from '@common/components';
import { NotResults } from '@dashboard/components';
import {
  GROUPING_CENTER,
  GROUPING_CLASS,
} from '@dashboard/constants/identification';
import {
  courseHasCognitiveSubprocesses,
  courseHasDifferentAges,
  getEvaluationAgeTotalRows,
  getFormattedAge,
  groupByCognitiveProcess,
} from '@dashboard/util/identification';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import classes from './IdentificationStatsFactory.module.scss';

export default function IdentificationStatsFactory({
  stats,
  isLoading,
  groupBy,
  getCourseTitle,
  getCourseHint,
  renderCourseStats,
}) {
  const { t } = useTranslation('dashboard\\IdentificationStatsView');
  const { t: publicationsT } = useTranslation('dashboard\\Publications');

  if (!stats) {
    return null;
  }

  if (isLoading) {
    return <Loader className={classes.loader} size="big" />;
  }

  const renderStatRow = (
    statRow,
    hasCognitiveSubprocesses,
    hasDifferentAges,
    evaluationAgeValue,
    evaluationAgeRowSpan,
    cognitiveProcessAgeValue,
    cognitiveProcessAgeRowSpan,
    cognitiveProcessRowSpan,
    globalRowIndex,
    evaluationAgeStartIndex,
    cognitiveProcessAgeStartIndex,
    cognitiveProcessStartIndex,
    bodyColumns,
  ) => {
    const key = `${evaluationAgeValue}_${cognitiveProcessAgeValue}-${
      statRow.cognitiveProcess
    }-${statRow.cognitiveSubprocess || ''}`;

    return (
      <tr key={key}>
        {globalRowIndex === evaluationAgeStartIndex ? (
          <td rowSpan={evaluationAgeRowSpan} className={classes.ageCell}>
            {getFormattedAge(evaluationAgeValue)}
          </td>
        ) : null}
        {hasDifferentAges &&
        globalRowIndex === cognitiveProcessAgeStartIndex ? (
          <td rowSpan={cognitiveProcessAgeRowSpan} className={classes.ageCell}>
            {getFormattedAge(cognitiveProcessAgeValue)}
          </td>
        ) : null}
        {globalRowIndex === cognitiveProcessStartIndex ? (
          <td rowSpan={cognitiveProcessRowSpan} className={classes.alignLeft}>
            {publicationsT(statRow.cognitiveProcess)}
          </td>
        ) : null}
        {hasCognitiveSubprocesses ? (
          <td className={classes.alignLeft}>
            {statRow.cognitiveSubprocess
              ? publicationsT(statRow.cognitiveSubprocess)
              : '-'}
          </td>
        ) : null}
        {bodyColumns.map((col) => (
          <td key={col.key} className={col.className}>
            {col.content}
          </td>
        ))}
      </tr>
    );
  };

  const renderCourseStatRows = (
    courseRow,
    hasCognitiveSubprocesses,
    hasDifferentAges,
    getBodyColumns,
  ) => {
    let globalRowIndex = 0;
    const rows = [];

    courseRow.data.forEach((evaluationAgeRow) => {
      const evaluationAgeStartIndex = globalRowIndex;
      const evaluationAgeRowSpan = getEvaluationAgeTotalRows(evaluationAgeRow);

      evaluationAgeRow.data.forEach((cognitiveProcessAgeRow) => {
        const cognitiveProcessAgeStartIndex = globalRowIndex;
        const cognitiveProcessAgeRowSpan = cognitiveProcessAgeRow.data.length;

        // Group by cognitive process to calculate rowspan
        const cognitiveProcessGroups = groupByCognitiveProcess(
          cognitiveProcessAgeRow.data,
        );

        Object.values(cognitiveProcessGroups).forEach((processRows) => {
          const cognitiveProcessStartIndex = globalRowIndex;
          const cognitiveProcessRowSpan = processRows.length;

          processRows.forEach((statRow) => {
            rows.push(
              renderStatRow(
                statRow,
                hasCognitiveSubprocesses,
                hasDifferentAges,
                evaluationAgeRow.evaluationAge,
                evaluationAgeRowSpan,
                cognitiveProcessAgeRow.cognitiveProcessAge,
                cognitiveProcessAgeRowSpan,
                cognitiveProcessRowSpan,
                globalRowIndex,
                evaluationAgeStartIndex,
                cognitiveProcessAgeStartIndex,
                cognitiveProcessStartIndex,
                getBodyColumns({ statRow }),
              ),
            );
            globalRowIndex += 1;
          });
        });
      });
    });

    return rows;
  };

  const renderStatsTable = ({
    courseRow,
    headerColumns,
    getBodyColumns,
    topHeaderColumnContent,
    topHeaderColumns,
  }) => {
    const hasCognitiveSubprocesses = courseHasCognitiveSubprocesses(courseRow);
    const hasDifferentAges = courseHasDifferentAges(courseRow);

    const renderTopHeader = () => {
      if (!topHeaderColumns || topHeaderColumns.length === 0) {
        return null;
      }

      let topHeaderColumnColspan = 2;
      if (hasDifferentAges) {
        topHeaderColumnColspan += 1; // For age column
      }
      if (hasCognitiveSubprocesses) {
        topHeaderColumnColspan += 1; // For cognitive subprocesses column
      }

      return (
        <tr>
          {topHeaderColumnContent ? (
            <th colSpan={topHeaderColumnColspan}>{topHeaderColumnContent}</th>
          ) : null}
          {topHeaderColumns.map((col) => (
            <th key={col.key} colSpan={col.colspan} className={col.className}>
              {col.content}
            </th>
          ))}
        </tr>
      );
    };

    return (
      <div className={classes.tableContainer}>
        <table className={classes.table} cellSpacing={0}>
          <thead>
            {renderTopHeader()}
            <tr>
              <th
                colSpan={hasDifferentAges ? 2 : 1}
                className={classes.ageCell}
              >
                {t('age')}
              </th>
              <th>{t('cognitiveProcess')}</th>
              {hasCognitiveSubprocesses ? (
                <th>{t('cognitiveSubprocesses')}</th>
              ) : null}
              {headerColumns.map((col) => (
                <th key={col.key} className={col.className}>
                  {col.content}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {renderCourseStatRows(
              courseRow,
              hasCognitiveSubprocesses,
              hasDifferentAges,
              getBodyColumns,
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const renderCourses = (data) => {
    return data.map(function (courseRow) {
      const courseTitle = getCourseTitle(courseRow);
      const courseHint = getCourseHint ? getCourseHint(courseRow) : null;

      return (
        <div key={courseRow.course}>
          <Title type="h3">
            <span style={{ textTransform: 'uppercase' }}>{courseTitle}</span>
            {courseRow.course !== 'all'
              ? `: "${publicationsT(courseRow.course)}"`
              : null}
          </Title>
          {courseHint ? (
            <Title type="p" color="grey-400">
              {courseHint}
            </Title>
          ) : null}
          {renderCourseStats({ courseRow, renderStatsTable })}
        </div>
      );
    });
  };

  const renderClasses = (data) => {
    return data.map(function (classRow) {
      return (
        <div key={classRow.classId}>
          <Title type="h2">
            {t('classGroup')}: {classRow.className}
          </Title>
          {renderCourses(classRow.data)}
        </div>
      );
    });
  };

  const renderCenters = (data) => {
    return data.map(function (centerRow) {
      let centerData;

      switch (groupBy) {
        case GROUPING_CLASS:
          centerData = renderClasses(centerRow.data);
          break;
        case GROUPING_CENTER:
          centerData = renderCourses(centerRow.data);
          break;
        default:
          centerData = renderCourses(data);
      }

      return (
        <div key={centerRow.centerId}>
          <Title type="h2">
            {t('center')}: {centerRow.centerName}
          </Title>
          {centerData}
        </div>
      );
    });
  };

  if (stats.data.length === 0) {
    return <NotResults card isLoading={false} />;
  }

  switch (groupBy) {
    case GROUPING_CENTER:
    case GROUPING_CLASS:
      return <>{renderCenters(stats.data)}</>;
    default:
      return <>{renderCourses(stats.data)}</>;
  }
}

IdentificationStatsFactory.propTypes = {
  stats: PropTypes.object,
  isLoading: PropTypes.bool,
  groupBy: PropTypes.string,
  getCourseTitle: PropTypes.func.isRequired,
  getCourseHint: PropTypes.func,
  renderCourseStats: PropTypes.func.isRequired,
};
